

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';
import { MapPin, Clock, ShoppingBag, Instagram, MessageSquare, ChevronDown, Rocket, Sparkles } from 'lucide-react';
import { CustomerLayout } from '../../components/ui/Layout';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { restaurant, products, isLoading, fetchRestaurantBySlug } = useStore();

  useEffect(() => {
    if (slug) fetchRestaurantBySlug(slug);
  }, [slug, fetchRestaurantBySlug]);

  if (isLoading || !restaurant) {
    return (
      <div className="min-h-screen bg-brand-yellow flex items-center justify-center border-[20px] border-black">
        <div className="w-24 h-24 border-[12px] border-black border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  const styles = restaurant.styles;
  const landing = restaurant.landing;
  const featuredProducts = products.filter(p => p.active).slice(0, 4);

  // ENGINE DE ESTILO DINÂMICO
  const designTokens = {
      '--primary': styles.primaryColor || '#FF005C',
      '--secondary': styles.secondaryColor || '#00F0FF',
      '--bg': styles.backgroundColor || '#E8E8E8',
      '--surface': styles.surfaceColor || '#FFFFFF',
      '--text': styles.textColor || '#000000',
      '--radius': styles.borderRadius === 'none' ? '0' : 
                  styles.borderRadius === 'small' ? '8px' :
                  styles.borderRadius === 'medium' ? '16px' :
                  styles.borderRadius === 'large' ? '32px' : '9999px',
      '--font-h': styles.fontHeading === 'serif' ? 'serif' : 
                  styles.fontHeading === 'mono' ? 'monospace' : 'Plus Jakarta Sans',
  };

  return (
    <CustomerLayout>
      <style>{`
        :root {
          ${Object.entries(designTokens).map(([k, v]) => `${k}: ${v};`).join('\n')}
        }
        .btn-brand { background: var(--primary); color: white; border-radius: var(--radius); border: 4px solid var(--text); }
        .font-brand-h { font-family: var(--font-h); }
      `}</style>

      <div className="min-h-screen bg-[var(--bg)] font-sans selection:bg-[var(--primary)] selection:text-white" style={{ color: 'var(--text)' }}>
        
        {/* HERO SECTION - ADAPTATIVA */}
        <section className={`relative min-h-screen flex items-center justify-center pt-48 pb-32 px-6 border-b-[12px] border-black ${landing?.layoutType === 'centered' ? 'text-center' : 'text-left'}`} style={{ backgroundColor: designTokens['--secondary'] }}>
          <div className={`max-w-7xl mx-auto w-full grid grid-cols-1 ${landing?.layoutType === 'split' ? 'lg:grid-cols-2' : ''} gap-20 items-center`}>
            
            <div className="space-y-12 animate-pop-in">
              <div className={`inline-block p-4 bg-white border-[8px] border-black shadow-bold rotate-[-3deg] animate-float`} style={{ borderRadius: 'var(--radius)' }}>
                 <img src={restaurant.logoUrl} alt="Logo" className="w-40 h-40 object-cover border-4 border-black" style={{ borderRadius: 'calc(var(--radius) / 2)' }} />
              </div>
              
              <div className="space-y-6">
                <h1 className="text-6xl md:text-9xl font-black tracking-tighter uppercase leading-[0.75] italic font-brand-h">
                  {landing?.heroTitle || 'SABOR REAL.'}<span className="text-[var(--primary)] animate-pulse">!</span>
                </h1>
                <p className="text-xl md:text-3xl font-black uppercase italic max-w-xl mx-auto lg:mx-0">
                  {landing?.heroSubtitle || 'A melhor experiência do mercado.'}
                </p>
              </div>

              <button 
                onClick={() => navigate(`/${restaurant.slug}/menu`)}
                className="btn-brand px-12 py-8 text-4xl italic tracking-tighter uppercase animate-vibrate flex items-center justify-center gap-6 shadow-bold-xl mx-auto lg:mx-0"
              >
                {landing?.ctaText || 'VER CARDÁPIO'} <ShoppingBag className="w-10 h-10" />
              </button>
            </div>
            
            {landing?.layoutType === 'split' && (
                <div className="hidden lg:block relative animate-pop-in">
                    <div className="aspect-square bg-white border-[18px] border-black shadow-bold-xl rotate-[4deg] overflow-hidden p-8" style={{ borderRadius: 'var(--radius)' }}>
                        <img src={restaurant.bannerUrl} className="w-full h-full object-cover border-[8px] border-black shadow-bold" alt="Banner" style={{ borderRadius: 'calc(var(--radius) / 2)' }} />
                    </div>
                </div>
            )}
          </div>
        </section>

        {/* BEST SELLERS - CONDICIONAL */}
        {landing?.showBestSellers && (
            <section className="py-32 px-6 max-w-7xl mx-auto">
                <header className="mb-24 flex flex-col md:flex-row justify-between items-end border-b-[8px] border-black pb-12 gap-8">
                    <h2 className="text-7xl font-black uppercase italic tracking-tighter font-brand-h">TOP <span style={{ color: 'var(--primary)' }}>ESCOLHAS.</span></h2>
                    <button onClick={() => navigate(`/${restaurant.slug}/menu`)} className="btn-brand px-8 py-4 font-black uppercase italic text-lg shadow-bold-sm">VER TUDO</button>
                </header>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                    {featuredProducts.map((p) => (
                        <div key={p.id} className="bg-[var(--surface)] border-[6px] border-black p-6 shadow-bold-sm group hover:translate-y-[-8px] transition-all" style={{ borderRadius: 'var(--radius)' }}>
                            <div className="aspect-square border-4 border-black mb-6 overflow-hidden" style={{ borderRadius: 'calc(var(--radius) / 2)' }}>
                                <img src={p.imageUrl} className="w-full h-full object-cover group-hover:scale-110 transition-transform" alt={p.name} />
                            </div>
                            <h4 className="text-2xl font-black uppercase italic mb-2 font-brand-h">{p.name}</h4>
                            <div className="bg-white border-2 border-black inline-block px-4 py-1 text-xl font-black italic">R$ {p.price.toFixed(2)}</div>
                        </div>
                    ))}
                </div>
            </section>
        )}

        {/* SOCIAL & LOCATION */}
        <section className="bg-black text-white py-32 px-6 border-y-[12px] border-black">
             <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
                 <div className="space-y-10">
                     <h3 className="text-6xl font-black italic uppercase font-brand-h">VISITE A <br/> <span style={{ color: 'var(--secondary)' }}>GENTE.</span></h3>
                     <div className="space-y-6">
                         <div className="flex items-center gap-6 text-2xl font-black italic">
                            <MapPin className="w-10 h-10" style={{ color: 'var(--primary)' }} /> {restaurant.address || 'Local não definido'}
                         </div>
                     </div>
                     <div className="flex gap-4">
                         {restaurant.social?.instagram && <SocialBtn icon={Instagram} link={restaurant.social.instagram} />}
                         {restaurant.social?.whatsapp && <SocialBtn icon={MessageSquare} link={restaurant.social.whatsapp} />}
                     </div>
                 </div>
                 {landing?.showFeatures && (
                     <div className="bg-[var(--secondary)] p-8 border-[8px] border-white shadow-bold-xl rotate-[-2deg]" style={{ borderRadius: 'var(--radius)' }}>
                         <h4 className="text-black text-4xl font-black uppercase italic mb-4">Experiência Única</h4>
                         <p className="text-black font-bold uppercase text-lg italic">Qualidade garantida em cada detalhe do nosso preparo.</p>
                     </div>
                 )}
             </div>
        </section>

        <footer className="py-20 bg-[var(--bg)] text-center">
            {/* FIX: Property 'name' does not exist on type 'InstagramProfile'. Use 'username' instead. */}
            <h5 className="text-6xl font-black uppercase italic tracking-tighter font-brand-h">{restaurant.username}<span style={{ color: 'var(--primary)' }}>.</span></h5>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] opacity-40 mt-8 italic">POWERED BY FLUXIL V6</p>
        </footer>
      </div>
    </CustomerLayout>
  );
};

const SocialBtn = ({ icon: Icon, link }: any) => (
    <a href={link} target="_blank" className="w-16 h-16 bg-white text-black border-4 border-black flex items-center justify-center hover:bg-black hover:text-white transition-all shadow-bold-sm" style={{ borderRadius: '12px' }}>
        <Icon className="w-8 h-8" />
    </a>
);

export default LandingPage;